import debounce from "lodash.debounce";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FormDashboardLabel as Label,
  FormDashboardInput as Input,
  FormDashboardInputNumber as InputNumber,
  FormDashboardTextarea as Textarea,
  Radio,
  FormRow,
  RequiredLabel,
  SpecialRequiredLabel,
} from "../../../components";
import { useInternationalization } from "../../i18n";
import { updateForm } from "../slice";

const FormProfile = () => {
  const dispatch = useDispatch()
  const form = useSelector(state => state.eventForm.data)
  const [defaultValues, setDefaultValues] = React.useState({})
  const { translate } = useInternationalization()

  React.useEffect(() => {
    setDefaultValues(form)
  }, [form])

  const {
    title_en,
    title_id,
    registrationType,
    external_link,
    guest_seat,
    registration_start_date,
    registration_start_time,
    registration_end_date,
    registration_end_time,
    event_start_date,
    event_start_time,
    event_end_date,
    event_end_time,
    about_en,
    about_id
  } = defaultValues

  const handleInput = debounce((e) => {
    if(e.target && e.target !== null){
      const key = e.target.name
      const value = e.target.value
      dispatch(updateForm({key, value}))
    }
  }, 250)

  const handleChange = (e) => {
    e.persist()
    handleInput(e)
  }

  const handleRadio = (e) => {
    handleInput(e)
  }

  return (
    <React.Fragment>
      <p className="text-lg font-bold mb-5 lg:mb-8">
        {translate('EVENT', 'profile')}
      </p>

      <FormRow>
        <Label>{translate('GENERAL', 'title')} ({translate('LANGUAGE', 'en')}) <RequiredLabel /></Label>
        <Input
          required
          className="w-full"
          name="title_en"
          defaultValue={title_en}
          onChange={handleChange}
        />
      </FormRow>

      <FormRow>
        <Label>{translate('GENERAL', 'title')} ({translate('LANGUAGE', 'id')}) <RequiredLabel /></Label>
        <Input
          required
          className="w-full"
          name="title_id"
          defaultValue={title_id}
          onChange={handleChange}
        />
      </FormRow>

      <FormRow>
        <Label>{translate('EVENT', 'registration_type')}</Label>
        <div className="flex items-center justify-between lg:justify-start mb-3 pt-1">
          <Radio
            name="registrationType"
            id="type1"
            label={translate('GENERAL', 'external_link')}
            value="external"
            className="mr-2 lg:mr-10"
            form={form}
            onChange={handleRadio}
          />
          <Radio
            name="registrationType"
            id="type2"
            label="Internal (via KHUB and Free)"
            value="internal"
            form={form}
            onChange={handleRadio}
          />
        </div>
        {
          registrationType === 'external' &&
          <Input
            className="w-full"
            placeholder="Link"
            name="external_link"
            defaultValue={external_link}
            onChange={handleChange}
          />
        }
        {
          registrationType === 'internal' &&
          <div className="flex items-center justify-end lg:justify-start my-2 lg:w-2/3 lg:ml-auto">
            <p className="mr-2 text-gray-444 text-sm">
              {translate('EVENT', 'guest_seat')}
            </p>
            <InputNumber
              className="w-32"
              defaultValue={guest_seat}
              onChange={(value) => {
                handleInput({
                  target: {
                    name: 'guest_seat',
                    value
                  }
                })
              }}
            />
          </div>
        }
      </FormRow>

      <FormRow>
        <Label>{translate('GENERAL', 'registration_date')} <RequiredLabel /></Label>
        <div className="flex flex-wrap -mx-4 mb-5 lg:mb-8">
          <div className="w-full lg:w-1/2 px-4 mb-5 lg:mb-0">
            <p className="text-sm mb-3">{translate('GENERAL', 'start_date')}</p>
            <Input
              required
              className="w-full mb-4"
              type="date"
              name="registration_start_date"
              defaultValue={registration_start_date}
              onChange={handleChange}
            />
            <Input
              required
              className="w-full"
              type="time"
              name="registration_start_time"
              defaultValue={registration_start_time}
              onChange={handleChange}
            />
          </div>
          <div className="w-full lg:w-1/2 px-4">
            <p className="text-sm mb-3">{translate('GENERAL', 'end_date')}</p>
            <Input
              required
              className="w-full mb-4"
              type="date"
              name="registration_end_date"
              min={registration_start_date}
              defaultValue={registration_end_date}
              onChange={handleChange}
            />
            <Input
              required
              className="w-full"
              type="time"
              name="registration_end_time"
              defaultValue={registration_end_time}
              onChange={handleChange}
            />
          </div>
        </div>
      </FormRow>

      {/* {
        showEventDate &&
      } */}
      <FormRow>
        <Label>{translate('EVENT', 'date')} <RequiredLabel /></Label>
        <div className="flex flex-wrap -mx-4 mb-5 lg:mb-8">
          <div className="w-full lg:w-1/2 px-4 mb-5 lg:mb-0">
            <p className="text-sm mb-3">{translate('GENERAL', 'start_date')}</p>
            <Input
              required
              className="w-full mb-4"
              type="date"
              name="event_start_date"
              defaultValue={event_start_date}
              onChange={handleChange}
            />
            <Input
              required
              className="w-full"
              type="time"
              name="event_start_time"
              defaultValue={event_start_time}
              onChange={handleChange}
            />
          </div>
          <div className="w-full lg:w-1/2 px-4">
            <p className="text-sm mb-3">{translate('GENERAL', 'end_date')}</p>
            <Input
              required
              className="w-full mb-4"
              type="date"
              name="event_end_date"
              defaultValue={event_end_date}
              min={event_start_date}
              onChange={handleChange}
            />
            <Input
              required
              className="w-full"
              type="time"
              name="event_end_time"
              defaultValue={event_end_time}
              onChange={handleChange}
            />
          </div>
        </div>
      </FormRow>

      <div className="mb-5 lg:mb-8">
        <p className="text-lg font-bold mb-4">{translate('NAVIGATION', 'about')}</p>
        <SpecialRequiredLabel />
      </div>

      <FormRow>
        <Label>{translate('NAVIGATION', 'about')} ({translate('LANGUAGE', 'en')})*</Label>
        <Textarea
          className="w-full"
          rows={10}
          name="about_en"
          defaultValue={about_en}
          onChange={handleChange}
        />
      </FormRow>
      <FormRow>
        <Label>{translate('NAVIGATION', 'about')} ({translate('LANGUAGE', 'id')})*</Label>
        <Textarea
          className="w-full"
          rows={10}
          name="about_id"
          defaultValue={about_id}
          onChange={handleChange}
        />
      </FormRow>
    </React.Fragment>
  )
}

export { FormProfile }
